'use client';

import React, { useMemo } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useMediaQuery } from 'usehooks-ts';

import Link from 'next/link';

import { Button } from '@/components/ui/button';
import {
    Pagination as PaginationCN,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';

type PaginationProps = {
    total: number;
    perPage?: number;
    currentPage: number;
};

export const Pagination = ({ total, currentPage, perPage = 6 }: PaginationProps) => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const t = useTranslations('Base');

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil((total || 0) / Math.max(1, perPage || 1))),
        [total, perPage]
    );

    if (!total || total <= 0) return null;

    const canGoPrev = currentPage > 1;
    const canGoNext = currentPage < totalPages;

    function getPaginationRange(totalPages: number, currentPage: number, delta: number = 2): string[] {
        if (totalPages <= 0) return [];

        const range: (number | string)[] = [];
        const left = Math.max(2, currentPage - delta);
        const right = Math.min(totalPages - 1, currentPage + delta);

        range.push(1); // первая страница всегда

        if (left > 2) range.push('...');

        for (let i = left; i <= right; i++) {
            range.push(i);
        }

        if (right < totalPages - 1) range.push('...');
        if (totalPages > 1) range.push(totalPages); // последняя страница всегда

        return range.map(String);
    }

    return (
        <PaginationCN>
            <PaginationContent>
                <PaginationItem>
                    <Button
                        variant="outline"
                        disabled={!canGoPrev}
                        className={cn({
                            'hover:shadow-sm': canGoPrev,
                        })}
                        size="sm"
                    >
                        <Link
                            href={`?page=${Math.max(1, currentPage - 1)}`}
                            className="flex flex-nowrap items-center"
                            aria-label={t('predydushayaSt')}
                        >
                            <ChevronLeftIcon className="mr-1 h-4 w-4" />
                            <p className="hidden md:inline-block sr-only">{t('predydushaya')}</p>
                        </Link>
                    </Button>
                </PaginationItem>
                {getPaginationRange(totalPages, currentPage, isMobile ? 1 : 2).map((item, idx) =>
                    item === '...' ? (
                        <PaginationItem key={`page-item-${item}-${idx}`} className="text-primary">
                            <PaginationEllipsis />
                        </PaginationItem>
                    ) : (
                        <PaginationItem key={`page-item-${item}-${idx}`}>
                            <Button asChild variant={item === String(currentPage) ? 'default' : 'outline'} size="sm">
                                <Link
                                    href={{
                                        pathname: '/news',
                                        query: {
                                            page: item,
                                        },
                                    }}
                                    aria-label={t('pereitiNaStranicu', { page: item })}
                                >
                                    {item}
                                </Link>
                            </Button>
                        </PaginationItem>
                    )
                )}
                <PaginationItem>
                    <Button
                        variant="outline"
                        disabled={!canGoNext}
                        className={cn({
                            'hover:shadow-sm': canGoNext,
                        })}
                        size="sm"
                    >
                        <Link
                            className="flex flex-nowrap items-center"
                            href={`?page=${Math.min(totalPages, currentPage + 1)}`}
                            aria-label={t('sleduyushayaSt')}
                        >
                            <p className="hidden md:inline-block sr-only">{t('sleduyushaya')}</p>
                            <ChevronRightIcon className="ml-1 h-4 w-4" />
                        </Link>
                    </Button>
                </PaginationItem>
            </PaginationContent>
        </PaginationCN>
    );
};

